import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";

import messages from "../../../../../assets/locale/messages";
import { UIActionModal } from "../../../../ui";
import { cancelCompanyPortalPaymentRequest } from "../../../../../network/apis/companyPortalEvents";
import { getPortalApiErrorMessage } from "../../../../../utils/companyPortal/eventApi";
import { showErrorMsg, showSuccessMsg } from "../../../../../utils/Helpers";
import PaymentNotice from "../../PaymentNotice";

import "../_CrewSizeUpgradeModals.scss";

/** Confirms and cancels the company's pending crew size change request; the event isn't touched. */
const CancelUpgradeRequestModal = ({ open, requestId, onSettled }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const t = events.viewEvents.crewSizeUpgrade.cancelModal;
  // Same refund notice as the publish request's cancel popup (PROLINE-4205).
  const { refundTitle, refundMessage } = events.viewEvents.cancelRequest;
  const [cancelling, setCancelling] = useState(false);

  const handleConfirm = async () => {
    setCancelling(true);
    try {
      const response = await cancelCompanyPortalPaymentRequest(
        requestId,
        // The error is toasted once below, not also by the interceptor.
        { handlerEnabled: false },
      );
      // The backend's message, or ours while it sends none.
      const message = response?.data?.message;
      showSuccessMsg(message || "upgradeRequestCancelledSuccessfully", Boolean(message));
    } catch (error) {
      // Usually the Super Admin reviewed the request in the meantime: the page is stale.
      showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
    } finally {
      // Either way the modal closes and the event is fetched again.
      setCancelling(false);
      onSettled();
    }
  };

  return (
    <UIActionModal
      open={open}
      // Keeping the request also refetches: the Super Admin may have reviewed it meanwhile.
      onClose={() => !cancelling && onSettled()}
      title={t.title}
      variant="reject"
      maxWidth="xs"
      actions={
        <UIActionModal.Actions
          cancelLabel={t.keep}
          confirmLabel={t.confirm}
          confirmVariant="reject"
          onCancel={onSettled}
          onConfirm={handleConfirm}
          confirmDisabled={cancelling}
        />
      }
    >
      <Typography component="p" className="text-body-sm crew-size-upgrade__message">
        {t.message}
      </Typography>
      <PaymentNotice title={refundTitle} description={refundMessage} />
    </UIActionModal>
  );
};

CancelUpgradeRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** `last_upgrade_payment_request.id` of the pending request. */
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** Called whenever the modal closes (kept, cancelled or refused): close and refetch the event. */
  onSettled: PropTypes.func.isRequired,
};

export default CancelUpgradeRequestModal;
